import { Flex, HStack, Box, useDisclosure, Text, Heading } from '@chakra-ui/react';
import {
  BaseButton,
  BaseDialog,
  BaseEmptyState,
  BaseHelpText,
  BasePopover,
  BaseToolbarButton
} from '@cks-internal/react-base';
import {
  faClock,
  faMessage,
  faPersonWalkingArrowRight,
  faRightFromBracket,
  faUserCheck,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';
import { StatusLine } from '../../features/workflow/components/StatusLine';
import { agentStatusDisplay } from '../../features/workflow/displayTypes';
import { SetScheduleForm } from '../../features/workflow/forms/ScheduleForm';
import { StatusForm } from '../../features/workflow/forms/StatusForm';
import { ScheduleTable } from '../../features/workflow/ScheduleTable';
import { ScoreButtons } from '../../features/workflow/ScoreButtons';
import { agentStatusType, ConnectionType } from '../../features/workflow/types';
import { useWorkflowSocket } from '../../features/workflow/hub/useWorkflowSocket';
import { useHoursFormat } from '../../features/workflow/utils/formatter';
import { wfWigitModes } from '../../features/workflow/CoordinatorWidget';
import { InactiveCheck } from '../../features/workflow/InactiveCheck';
import { useGroupName } from '../../features/workflow/utils/useGroupName';
import { ConnectionChange } from '../../features/workflow/ConnectionChange';

export const CurrentUser = () => {
  const invoke = useWorkflowSocket((r) => r.invoke);
  const user = useWorkflowSocket((r) => r.data?.user);
  const schedules = useWorkflowSocket((r) => r.data?.schedule);
  const shiftString = useHoursFormat(user?.Shift);
  const lunchString = useHoursFormat(user?.Lunch);

  const scheduleTableDialog = useDisclosure();
  const setScheduleForm = useDisclosure();
  const showStatusMessage = useDisclosure();
  const statusMessageFrom = useDisclosure();
  const statusDisplay = agentStatusDisplay[user?.Status as agentStatusType];
  const groupName = useGroupName(user?.Group);
  const manager = useMemo(() => {
    if (user?.ConnectionType == ConnectionType.Coordinator) {
      return 'You';
    }
    const manager = Object.entries(schedules ?? {}).find(([_, value]) => value.IsCoordinator);
    if (manager != undefined) {
      const user = manager[1];
      return user.Name;
    }
    return 'Not Set';
  }, [schedules]);
  if (user == undefined) {
    return (
      <BaseButton
        onClick={() => {
          invoke('Leave');
        }}>
        Leave Workflow
      </BaseButton>
    );
  }
  return (
    <>
      <InactiveCheck />
      <ConnectionChange />
      <Flex h={'70px'} alignContent={'center'} p={'8px'} gap={'8px'} shadow={'lg'} bgColor={statusDisplay?.bgColor}>
        <Flex flexDir={'column'}>
          <BaseToolbarButton
            icon={faUsers}
            w={'fit-content'}
            h={'fit-content'}
            p={'4px'}
            onClick={() => {
              scheduleTableDialog.onToggle();
            }}
            // menuItems={[
            //   {
            //     text: `Set Self to ${user.Status != agentStatusType.Away ? 'Away' : 'Ready'}`,
            //     icon: user.Status != agentStatusType.Away ? faRightFromBracket : faUserCheck,
            //     onClick() {
            //       if (user.Status != agentStatusType.Away) {
            //         statusMessageFrom.onOpen();
            //         return;
            //       }
            //       if (user.Id) {
            //         invoke('UpdateStatus', agentStatusType.Ready, user.Id, undefined);
            //       }
            //     }
            //   },
            //   {
            //     text: `Check Status Message`,
            //     icon: faMessage,
            //     onClick() {
            //       showStatusMessage.onOpen();
            //     }
            //   },
            //   {
            //     text: 'Set My Schedules',
            //     icon: faClock,
            //     onClick() {
            //setScheduleForm.onOpen();
            //     }
            //   },
            //   {
            //     icon: faUserXmark,
            //     text: 'Leave',
            //     onClick() {
            //setScheduleForm.onOpen();
            //     }
            //   }
            // ]}>
          >
            {groupName} Schedules
          </BaseToolbarButton>
          <Flex w={'100%'} justifyContent={'space-between'}>
            <BaseToolbarButton
              h={'fit-content'}
              w={'fit-content'}
              p={'4px'}
              icon={user.Status != agentStatusType.Away ? faPersonWalkingArrowRight : faUserCheck}
              toolTip={`Set Self to ${user.Status != agentStatusType.Away ? 'Away' : 'Ready'}`}
              onClick={() => {
                if (user.Status != agentStatusType.Away) {
                  statusMessageFrom.onOpen();
                  return;
                }
                if (user.Id) {
                  invoke('UpdateStatus', agentStatusType.Ready, user.Id, undefined);
                }
              }}
            />
            <BaseToolbarButton
              h={'fit-content'}
              w={'fit-content'}
              p={'4px'}
              icon={faMessage}
              toolTip={`Check Status Message`}
              onClick={() => {
                showStatusMessage.onOpen();
              }}
            />
            <BaseToolbarButton
              h={'fit-content'}
              w={'fit-content'}
              p={'4px'}
              toolTip={'Set My Schedules'}
              icon={faClock}
              onClick={() => {
                setScheduleForm.onOpen();
              }}
            />
            <BasePopover
              onOk={() => {
                invoke('Leave', false);
              }}
              okayButtonText='Leave'
              onCancel={() => {}}
              trigger={
                <BaseToolbarButton
                  h={'fit-content'}
                  toolTip={'Leave Workflow Tool'}
                  w={'fit-content'}
                  p={'4px'}
                  icon={faRightFromBracket}
                />
              }>
              <div>Are you sure you want to leave the workflow tool?</div>
            </BasePopover>
          </Flex>
        </Flex>
        <Flex flexDir={'column'} ml={'8px'} gap={'2px'} divideColor={'blackAlpha.500'} divideY={'1px'}>
          <HStack w={'100%'}>
            <StatusLine title='Active Manager' status={manager} />
          </HStack>
          <HStack w={'100%'} justifyContent={'space-between'}>
            <StatusLine title='Shift' status={`${shiftString.start} - ${shiftString.end}`} />
          </HStack>
          <HStack w={'100%'} justifyContent={'space-between'}>
            <StatusLine title='Break' status={`${lunchString.start} - ${lunchString.end}`} />
          </HStack>
        </Flex>
        <HStack gap={'4px'}>
          <ScoreButtons user={user} buttonArea={'40px'} mode={wfWigitModes.Lock} />
        </HStack>
      </Flex>
      <SetScheduleForm
        shift={user.Shift}
        IsCoordinator={user.ConnectionType == ConnectionType.Coordinator}
        lunch={user.Lunch}
        id={user.Id}
        disclosure={setScheduleForm}
        children={<Box />}
      />
      <BaseDialog
        header={
          <Heading>
            Schedules <BaseHelpText text={'Sorting shows the next upcoming time'} />
          </Heading>
        }
        disclosure={scheduleTableDialog}
        dragDialog={{}}>
        <ScheduleTable />
      </BaseDialog>
      <BaseDialog header='Status Message' disclosure={showStatusMessage} dragDialog={{}}>
        {user.Status == agentStatusType.Away && user.StatusMessage && <Text>{user.StatusMessage}</Text>}
        {user.Status != agentStatusType.Away && (
          <BaseEmptyState title='You Have No Status Message' description='Ready Users Do Not Have Status Messages' />
        )}
      </BaseDialog>
      {user.Status != undefined && user.Id && <StatusForm disclosure={statusMessageFrom} userId={user.Id} />}
    </>
  );
};
